import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Avatar,
  Button,
  CircularProgress,
  Divider,
  Container,
  Paper,
  TextField,
  Typography,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Box,
  Stack,
} from '@mui/material'
import {
  ArrowOutward,
  CheckCircle,
  BarChart,
  CalendarMonth,
  LocalDining,
  MonetizationOn,
  NorthEast,
  PendingActions,
  Schedule,
  ReceiptLong,
  TrendingUp,
  WarningAmber,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

import PageLoader from '../components/ui/PageLoader'
import CrmButton from '../components/ui/CrmButton'
import { CrmInput, CrmSelect } from '../components/ui/CrmFormControls'
import '../components/listing/crm-listing.scss';
import '../assets/styles/Dashboard.scss'

import { getInvoices } from '../services/invoiceService'
import { fetchQuotations } from '../services/quotationService'
import { fetchAllProducts } from '../services/productServices'
import { fetchLeads } from '../services/leadService'
import { listOrders } from '../services/orderService'
import { fetchKots } from '../services/kotService'
import { useSettings } from '../context/SettingsContext'
import { formatDate as formatLocalDate, parseDateInput } from '../utils/dateFormatter'
import { formatStatusLabel } from '../utils/statusFormatter'
import useAutoRefresh from '../hooks/useAutoRefresh'

const DASHBOARD_PENDING_INVOICES_PAGE_KEY = 'dashboardPendingInvoicesPage'
const DASHBOARD_PENDING_INVOICES_ROWS_KEY = 'dashboardPendingInvoicesRowsPerPage'
const DASHBOARD_TREND_RANGE_KEY = 'dashboardTrendRangeMonths'
const DASHBOARD_PENDING_FILTER_KEY = 'dashboardPendingInvoicesFilter'
const DASHBOARD_SELECTED_MONTH_KEY = 'dashboardSelectedMonth'

const Dashboard = () => {
  const navigate = useNavigate()
  const { settings } = useSettings()
  const [loading, setLoading] = useState(true)

  const [invoices, setInvoices] = useState([])
  const [quotations, setQuotations] = useState([])
  const [products, setProducts] = useState([])
  const [leads, setLeads] = useState([])
  const [orders, setOrders] = useState([])
  const [kots, setKots] = useState([])
  const [pendingInvoicesPage, setPendingInvoicesPage] = useState(() => {
    const savedPage = Number(sessionStorage.getItem(DASHBOARD_PENDING_INVOICES_PAGE_KEY));
    return Number.isInteger(savedPage) && savedPage >= 0 ? savedPage : 0;
  })
  const [pendingInvoicesRowsPerPage, setPendingInvoicesRowsPerPage] = useState(() => {
    const savedRows = Number(sessionStorage.getItem(DASHBOARD_PENDING_INVOICES_ROWS_KEY))
    return [5, 10, 15].includes(savedRows) ? savedRows : 5
  })
  const [trendRangeMonths, setTrendRangeMonths] = useState(() => {
    const savedRange = Number(sessionStorage.getItem(DASHBOARD_TREND_RANGE_KEY))
    return [3, 6, 12].includes(savedRange) ? savedRange : 6
  })
  const [pendingInvoiceFilter, setPendingInvoiceFilter] = useState(() => {
    const savedFilter = sessionStorage.getItem(DASHBOARD_PENDING_FILTER_KEY)
    return ['all', 'issued', 'overdue'].includes(savedFilter) ? savedFilter : 'all'
  })
  const [selectedMonthKey, setSelectedMonthKey] = useState(() => {
    const savedMonth = sessionStorage.getItem(DASHBOARD_SELECTED_MONTH_KEY)
    if (/^\d{4}-\d{2}$/.test(savedMonth || '')) {
      return savedMonth
    }
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  })
  const hasLoadedOnceRef = useRef(false)

  const getListSignature = (items, pick) => {
    if (!Array.isArray(items)) return '[]'

    return JSON.stringify(items.map((item) => pick(item || {})))
  }

  const isCateringBusiness = settings?.business_type === 'CATERING';

  /* =======================
     FORMAT HELPERS
  ======================= */

  const formatINR = (amount) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0)

  const formatDate = (date) =>
    date ? formatLocalDate(date) : '-'

  function getInvoiceDate(inv) {
    return parseDateInput(inv?.issue_date || inv?.created_at || inv?.date || 0)
  }

  function isValidDate(date) {
    return date instanceof Date && !Number.isNaN(date.getTime())
  }

  /* =======================
     LOAD DATA
  ======================= */

  const loadData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent && !hasLoadedOnceRef.current) {
        setLoading(true)
      }

      const promises = [
        getInvoices(),
        fetchQuotations(),
        fetchAllProducts(),
        fetchLeads(),
        listOrders(),
      ];

      // Only fetch KOTs if business type is CATERING
      if (isCateringBusiness) {
        promises.push(fetchKots('all'));
      }

      const results = await Promise.all(promises);

      const [
        invoicesRes,
        quotationsRes,
        productsRes,
        leadsRes,
        ordersRes,
        kotsRes
      ] = results;

      const nextInvoices = Array.isArray(invoicesRes)
        ? invoicesRes
        : invoicesRes?.data || []

      setInvoices((prev) => {
        const prevSignature = getListSignature(prev, (item) => ({
          id: Number(item?.id || 0),
          status: String(item?.status || ''),
          issue_date: String(item?.issue_date || ''),
          grand_total: Number(item?.grand_total || 0),
        }))
        const nextSignature = getListSignature(nextInvoices, (item) => ({
          id: Number(item?.id || 0),
          status: String(item?.status || ''),
          issue_date: String(item?.issue_date || ''),
          grand_total: Number(item?.grand_total || 0),
        }))
        return prevSignature === nextSignature ? prev : nextInvoices
      })

      const nextQuotations = Array.isArray(quotationsRes)
        ? quotationsRes
        : quotationsRes?.data || []

      setQuotations((prev) => {
        const prevSignature = getListSignature(prev, (item) => ({
          id: Number(item?.id || 0),
          status: String(item?.status || ''),
          created_at: String(item?.created_at || ''),
          grand_total: Number(item?.grand_total || 0),
        }))
        const nextSignature = getListSignature(nextQuotations, (item) => ({
          id: Number(item?.id || 0),
          status: String(item?.status || ''),
          created_at: String(item?.created_at || ''),
          grand_total: Number(item?.grand_total || 0),
        }))
        return prevSignature === nextSignature ? prev : nextQuotations
      })

      const nextProducts = Array.isArray(productsRes)
        ? productsRes
        : productsRes?.data || []

      setProducts((prev) => {
        const prevSignature = getListSignature(prev, (item) => ({
          id: Number(item?.id || 0),
          name: String(item?.name || ''),
          updated_at: String(item?.updated_at || ''),
        }))
        const nextSignature = getListSignature(nextProducts, (item) => ({
          id: Number(item?.id || 0),
          name: String(item?.name || ''),
          updated_at: String(item?.updated_at || ''),
        }))
        return prevSignature === nextSignature ? prev : nextProducts
      })

      const nextLeads = Array.isArray(leadsRes)
        ? leadsRes
        : leadsRes?.leads || leadsRes?.data || []

      setLeads((prev) => {
        const prevSignature = getListSignature(prev, (item) => ({
          id: Number(item?.id || 0),
          updated_at: String(item?.updated_at || ''),
          status: String(item?.status || ''),
        }))
        const nextSignature = getListSignature(nextLeads, (item) => ({
          id: Number(item?.id || 0),
          updated_at: String(item?.updated_at || ''),
          status: String(item?.status || ''),
        }))
        return prevSignature === nextSignature ? prev : nextLeads
      })
      // KOTs (only if catering business)
      if (isCateringBusiness && kotsRes) {
        const nextKots = Array.isArray(kotsRes)
          ? kotsRes
          : kotsRes?.kots || kotsRes?.data || []

        setKots((prev) => {
          const prevSignature = getListSignature(prev, (item) => ({
            id: Number(item?.id || 0),
            status: String(item?.status || ''),
            updated_at: String(item?.updated_at || ''),
          }))
          const nextSignature = getListSignature(nextKots, (item) => ({
            id: Number(item?.id || 0),
            status: String(item?.status || ''),
            updated_at: String(item?.updated_at || ''),
          }))
          return prevSignature === nextSignature ? prev : nextKots
        })
      }


      const nextOrders = Array.isArray(ordersRes)
        ? ordersRes
        : ordersRes?.orders || ordersRes?.data || []

      setOrders((prev) => {
        const prevSignature = getListSignature(prev, (item) => ({
          id: Number(item?.id || 0),
          status: String(item?.status || ''),
          created_at: String(item?.created_at || ''),
          total_amount: Number(item?.total_amount || item?.grand_total || 0),
        }))
        const nextSignature = getListSignature(nextOrders, (item) => ({
          id: Number(item?.id || 0),
          status: String(item?.status || ''),
          created_at: String(item?.created_at || ''),
          total_amount: Number(item?.total_amount || item?.grand_total || 0),
        }))
        return prevSignature === nextSignature ? prev : nextOrders
      })

    } catch (error) {
      console.error('Dashboard load error:', error)
    } finally {
      if (!hasLoadedOnceRef.current) {
        setLoading(false)
        hasLoadedOnceRef.current = true
      }
    }
  }, [isCateringBusiness])

  useEffect(() => {
    loadData({ silent: false })
  }, [loadData])

  const handleDashboardAutoRefresh = useCallback(() => {
    return loadData({ silent: true })
  }, [loadData])

  useAutoRefresh(handleDashboardAutoRefresh, {
    intervalMs: 30000,
    watch: [isCateringBusiness],
  })

  /* =======================
     CALCULATIONS
  ======================= */

  const totalInvoiceAmount = invoices.reduce(
    (sum, inv) => sum + Number(inv.grand_total || 0),
    0
  )

  // Selected month derived dates (used by pending invoice filters)
  const [selectedYear, selectedMonthIndex] = selectedMonthKey.split('-').map(Number)
  const selectedMonthDate = new Date(selectedYear, Math.max(0, selectedMonthIndex - 1), 1)
  const currentMonthStart = new Date(selectedMonthDate.getFullYear(), selectedMonthDate.getMonth(), 1)
  const previousMonthStart = new Date(selectedMonthDate.getFullYear(), selectedMonthDate.getMonth() - 1, 1)
  const nextMonthStart = new Date(selectedMonthDate.getFullYear(), selectedMonthDate.getMonth() + 1, 1)
  const monthFormatter = new Intl.DateTimeFormat('en-IN', { month: 'short' })

  // helper: determine if invoice is overdue
  function isOverdueInvoice(inv) {
    if (typeof inv?.is_overdue === 'boolean') return inv.is_overdue

    const status = String(inv?.status || '').trim().toLowerCase()
    if (status === 'paid' || status === 'cancelled') return false

    const grand = Number(inv?.grand_total || 0)
    let paid = Number(inv?.paid_amount || 0)
    if (!paid && Array.isArray(inv?.payments) && inv.payments.length) {
      paid = inv.payments.reduce((s, p) => {
        if (String(p.paymentType || '').toUpperCase() === 'OTHER') return s
        return s + Number(p.amount || 0)
      }, 0)
    }
    const balanceDue = Math.max(0, grand - paid)
    if (balanceDue <= 0) return false

    const dueDate = parseDateInput(inv?.due_date || inv?.dueDate || inv?.issue_date || 0)
    return dueDate instanceof Date && !Number.isNaN(dueDate.getTime()) && dueDate < new Date()
  }

  // Include invoices that are overdue regardless of the issue month,
  // and also include issued invoices from the selected month with a positive balance.
  const pendingInvoices = invoices
    .filter((inv) => {
      const date = getInvoiceDate(inv)

      // Determine balance due robustly: prefer backend `balance_due`, else compute from payments
      const grand = Number(inv?.grand_total || 0)
      let paid = Number(inv?.paid_amount || 0)
      if (!paid && Array.isArray(inv?.payments) && inv.payments.length) {
        paid = inv.payments.reduce((s, p) => {
          if (String(p.paymentType || '').toUpperCase() === 'OTHER') return s
          return s + Number(p.amount || 0)
        }, 0)
      }
      const balanceDue = Math.max(0, grand - paid)

      const status = String(inv?.status || '').trim().toLowerCase()
      const isIssued = status === 'issued'
      const inSelectedMonth = isValidDate(date) && date >= currentMonthStart && date < nextMonthStart

      // Include if invoice is overdue (server or computed) OR it's an issued invoice within selected month with balance
      if (isOverdueInvoice(inv)) return true
      if (isIssued && inSelectedMonth && balanceDue > 0) return true
      return false
    })
    .sort(
      (a, b) =>
        (parseDateInput(b.issue_date || 0) || new Date(0)) -
        (parseDateInput(a.issue_date || 0) || new Date(0))
    )

  const pendingKotsCount = kots.filter(
    (k) => String(k?.status || '').toLowerCase() === 'pending'
  ).length

  const filteredPendingInvoices = pendingInvoices.filter((inv) => {
    if (pendingInvoiceFilter === 'issued') {
      return String(inv?.status || '').trim().toLowerCase() === 'issued'
    }
    if (pendingInvoiceFilter === 'overdue') {
      return isOverdueInvoice(inv)
    }
    return true
  })

  const paginatedPendingInvoices = filteredPendingInvoices.slice(
    pendingInvoicesPage * pendingInvoicesRowsPerPage,
    pendingInvoicesPage * pendingInvoicesRowsPerPage + pendingInvoicesRowsPerPage
  )

  const handlePendingInvoicesPageChange = (_event, nextPage) => {
    setPendingInvoicesPage(nextPage)
  }

  const handlePendingInvoicesRowsPerPageChange = (event) => {
    setPendingInvoicesRowsPerPage(parseInt(event.target.value, 10))
    setPendingInvoicesPage(0)
  }

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredPendingInvoices.length / pendingInvoicesRowsPerPage) - 1)
    if (pendingInvoicesPage > maxPage) {
      setPendingInvoicesPage(maxPage)
    }
  }, [filteredPendingInvoices.length, pendingInvoicesPage, pendingInvoicesRowsPerPage])

  useEffect(() => {
    setPendingInvoicesPage(0)
  }, [pendingInvoiceFilter])

  useEffect(() => {
    sessionStorage.setItem(DASHBOARD_PENDING_INVOICES_PAGE_KEY, String(pendingInvoicesPage))
  }, [pendingInvoicesPage])

  useEffect(() => {
    sessionStorage.setItem(DASHBOARD_PENDING_INVOICES_ROWS_KEY, String(pendingInvoicesRowsPerPage))
  }, [pendingInvoicesRowsPerPage])

  useEffect(() => {
    sessionStorage.setItem(DASHBOARD_TREND_RANGE_KEY, String(trendRangeMonths))
  }, [trendRangeMonths])

  useEffect(() => {
    sessionStorage.setItem(DASHBOARD_PENDING_FILTER_KEY, pendingInvoiceFilter)
  }, [pendingInvoiceFilter])

  useEffect(() => {
    sessionStorage.setItem(DASHBOARD_SELECTED_MONTH_KEY, selectedMonthKey)
  }, [selectedMonthKey])

  const leadQualifiedCount = leads.filter(
    (lead) => String(lead?.status || '').toLowerCase() === 'qualified'
  ).length

  const leadConvertedCount = leads.filter((lead) => {
    const status = String(lead?.status || '').toLowerCase()
    return status === 'won' || status === 'converted'
  }).length

  const invoiceCollectionRate = invoices.length
    ? Math.round(
      (invoices.filter((inv) => String(inv?.status || '').trim().toLowerCase() === 'paid').length /
        invoices.length) *
      100
    )
    : 0

  // selected-month dates and helpers are defined earlier above

  const thisMonthRevenue = invoices.reduce((sum, inv) => {
    const date = getInvoiceDate(inv)
    if (!isValidDate(date) || date < currentMonthStart || date >= nextMonthStart) {
      return sum
    }
    return sum + Number(inv?.grand_total || 0)
  }, 0)

  const lastMonthRevenue = invoices.reduce((sum, inv) => {
    const date = getInvoiceDate(inv)
    if (!isValidDate(date) || date < previousMonthStart || date >= currentMonthStart) {
      return sum
    }
    return sum + Number(inv?.grand_total || 0)
  }, 0)

  const revenueDeltaPercent = lastMonthRevenue > 0
    ? Math.round(((thisMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100)
    : thisMonthRevenue > 0
      ? 100
      : 0

  const averageInvoiceValue = invoices.length
    ? Math.round(totalInvoiceAmount / invoices.length)
    : 0

  const revenueTrend = Array.from({ length: trendRangeMonths }, (_, index) => {
    const date = new Date(selectedMonthDate.getFullYear(), selectedMonthDate.getMonth() - (trendRangeMonths - 1 - index), 1)
    const start = new Date(date.getFullYear(), date.getMonth(), 1)
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 1)

    const value = invoices.reduce((sum, inv) => {
      const invoiceDate = getInvoiceDate(inv)
      if (!isValidDate(invoiceDate) || invoiceDate < start || invoiceDate >= end) {
        return sum
      }
      return sum + Number(inv?.grand_total || 0)
    }, 0)

    return {
      label: monthFormatter.format(date),
      value,
    }
  })

  const selectedRangeRevenue = revenueTrend.reduce((sum, month) => sum + Number(month?.value || 0), 0)
  const selectedRangeAverage = revenueTrend.length ? Math.round(selectedRangeRevenue / revenueTrend.length) : 0

  const paidInvoicesCount = invoices.filter((inv) => String(inv?.status || '').trim().toLowerCase() === 'paid').length
  const issuedInvoicesCount = invoices.filter((inv) => String(inv?.status || '').trim().toLowerCase() === 'issued').length
  const cancelledInvoicesCount = invoices.filter((inv) => String(inv?.status || '').trim().toLowerCase() === 'cancelled').length

  const overdueInvoicesCount = invoices.filter((inv) => isOverdueInvoice(inv)).length

  const activeInvoicesCount = Math.max(0, invoices.length - paidInvoicesCount - cancelledInvoicesCount)
  const overdueShare = activeInvoicesCount > 0
    ? Math.round((overdueInvoicesCount / activeInvoicesCount) * 100)
    : 0

  const totalPaidAmount = invoices.reduce((sum, inv) => {
    const status = String(inv?.status || '').trim().toLowerCase()
    return status === 'paid' ? sum + Number(inv?.grand_total || 0) : sum
  }, 0)

  const totalPendingAmount = pendingInvoices.reduce(
    (sum, inv) => sum + Number(inv?.grand_total || 0),
    0
  )

  const activeOrdersCount = orders.filter((order) => !['completed', 'cancelled'].includes(String(order?.status || '').toLowerCase())).length

  const topPendingInvoicesByAmount = [...pendingInvoices]
    .sort((a, b) => Number(b?.grand_total || 0) - Number(a?.grand_total || 0))
    .slice(0, 4)

  const currentMonthInvoiceCount = invoices.filter((inv) => {
    const date = getInvoiceDate(inv)
    return isValidDate(date) && date >= currentMonthStart && date < nextMonthStart
  }).length

  const lastMonthInvoiceCount = invoices.filter((inv) => {
    const date = getInvoiceDate(inv)
    return isValidDate(date) && date >= previousMonthStart && date < currentMonthStart
  }).length

  const invoiceVelocityPercent = lastMonthInvoiceCount > 0
    ? Math.round(((currentMonthInvoiceCount - lastMonthInvoiceCount) / lastMonthInvoiceCount) * 100)
    : currentMonthInvoiceCount > 0
      ? 100
      : 0

  const buildSparklinePath = (items) => {
    const width = 520
    const height = 130
    const padding = 10
    if (!items.length) return ''

    const stepX = (width - padding * 2) / Math.max(1, items.length - 1)
    const maxValue = Math.max(...items.map((i) => i.value), 1)

    return items
      .map((item, index) => {
        const x = padding + stepX * index
        const y = height - padding - ((item.value || 0) / maxValue) * (height - padding * 2)
        return `${index === 0 ? 'M' : 'L'} ${x} ${y}`
      })
      .join(' ')
  }

  const sparklinePath = buildSparklinePath(revenueTrend)

  const kpiCards = [
    {
      key: 'leads',
      title: 'Contacts',
      value: leads.length,
      color: '#0288d1',
      path: '/leads',
      accent: 'linear-gradient(120deg, #e0f7ff 0%, #f4fbff 100%)',
      icon: <TrendingUp fontSize="small" />,
      meta: `${leadQualifiedCount} qualified`,
    },
    {
      key: 'quotations',
      title: 'Quotations',
      value: quotations.length,
      color: '#6a1b9a',
      path: '/quotations',
      accent: 'linear-gradient(120deg, #f5e8ff 0%, #fcf6ff 100%)',
      icon: <ReceiptLong fontSize="small" />,
      meta: `${leadConvertedCount} converted`,
    },
    {
      key: 'orders',
      title: 'Orders',
      value: orders.length,
      color: '#ef6c00',
      path: '/orders',
      accent: 'linear-gradient(120deg, #ffedd8 0%, #fff8ef 100%)',
      icon: <PendingActions fontSize="small" />,
      meta: `${activeOrdersCount} active`,
    },
    {
      key: 'products',
      title: 'Products',
      value: products.length,
      color: '#00897b',
      path: '/products/list',
      accent: 'linear-gradient(120deg, #dffaf5 0%, #f2fffc 100%)',
      icon: <ArrowOutward fontSize="small" />,
      meta: `${products.length} items`,
    },
  ]

  if (isCateringBusiness) {
    kpiCards.push({
      key: 'pending-kots',
      title: 'Pending KOTs',
      value: pendingKotsCount,
      color: '#9c27b0',
      path: '/kots',
      accent: 'linear-gradient(120deg, #f7e8ff 0%, #fdf7ff 100%)',
      icon: <LocalDining fontSize="small" />,
      meta: `${pendingKotsCount} open`,
    })
  }

  kpiCards.push({
    key: 'total-invoice-amount',
    title: 'Total Invoice Amount',
    value: formatINR(totalInvoiceAmount),
    color: '#2e7d32',
    path: '/invoices',
    accent: 'linear-gradient(120deg, #e5ffe8 0%, #f4fff4 100%)',
    icon: <ReceiptLong fontSize="small" />,
    meta: `${invoiceCollectionRate}% collection rate`,
  })

  /* =======================
     CRM DASHBOARD VIEW
  ======================= */

  return (
    <main className="crm-dashboard">
      {loading ? <PageLoader message="Loading dashboard..." minHeight={300} /> : <>
        <header className="crm-dashboard__header">
          <div>
            <span className="crm-dashboard__eyebrow">Dashboard</span>
            <h1>Revenue & operations</h1>
            <p>One view of contacts, commercial activity, collections and fulfilment.</p>
          </div>
          <div className="crm-dashboard__header-actions">
            <CrmInput label="Month" type="month" value={selectedMonthKey} onChange={(event) => setSelectedMonthKey(event.target.value)} />
            <CrmSelect label="Trend range" value={String(trendRangeMonths)} onChange={(event) => setTrendRangeMonths(Number(event.target.value))}>
              <option value="3">3 months</option><option value="6">6 months</option><option value="12">12 months</option>
            </CrmSelect>
            <CrmButton variant="secondary" onClick={() => loadData({ silent: false })}>Refresh</CrmButton>
          </div>
        </header>

        <section className="crm-dashboard__kpis">
          {kpiCards.map((card) => <button key={card.key} type="button" className="crm-dashboard-kpi" onClick={() => navigate(card.path)}>
            <div className="crm-dashboard-kpi__top"><span>{card.title}</span><span className="crm-dashboard-kpi__icon">{card.icon}</span></div>
            <strong>{card.value}</strong><small>{card.meta}</small>
          </button>)}
        </section>

        <section className="crm-dashboard__grid crm-dashboard__grid--main">
          <article className="crm-dashboard-card crm-dashboard-card--trend">
            <div className="quotation-section-heading"><div><h2>Revenue trend</h2><p>{trendRangeMonths}-month invoice value through the selected month.</p></div><div className={`crm-dashboard__delta ${revenueDeltaPercent < 0 ? 'is-negative' : ''}`}>{revenueDeltaPercent >= 0 ? '+' : ''}{revenueDeltaPercent}%</div></div>
            <div className="crm-dashboard__trend-summary"><div><span>Range revenue</span><strong>{formatINR(selectedRangeRevenue)}</strong></div><div><span>Monthly average</span><strong>{formatINR(selectedRangeAverage)}</strong></div><div><span>Collection rate</span><strong>{invoiceCollectionRate}%</strong></div></div>
            <div className="crm-dashboard-chart" aria-label="Revenue trend chart">
              <svg viewBox="0 0 520 130" role="img"><path d={sparklinePath} fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg>
              <div className="crm-dashboard-chart__labels">{revenueTrend.map((item) => <span key={item.label}>{item.label}<small>{formatINR(item.value)}</small></span>)}</div>
            </div>
          </article>

          <article className="crm-dashboard-card">
            <div className="quotation-section-heading"><div><h2>Collections health</h2><p>Current payment and invoice risk.</p></div><CrmButton size="small" variant="secondary" onClick={() => navigate('/invoices')}>View invoices</CrmButton></div>
            <div className="crm-dashboard-health">
              <div><span>Paid</span><strong>{paidInvoicesCount}</strong><small>{formatINR(totalPaidAmount)}</small></div>
              <div><span>Issued</span><strong>{issuedInvoicesCount}</strong><small>{formatINR(totalPendingAmount)}</small></div>
              <div><span>Overdue</span><strong>{overdueInvoicesCount}</strong><small>{overdueShare}% of active</small></div>
              <div><span>Cancelled</span><strong>{cancelledInvoicesCount}</strong><small>{activeInvoicesCount} active</small></div>
            </div>
            <div className="crm-dashboard__progress"><div style={{ width: `${Math.max(0, Math.min(100, invoiceCollectionRate))}%` }} /></div>
          </article>
        </section>

        <section className="crm-dashboard__grid crm-dashboard__grid--lower">
          <article className="crm-dashboard-card crm-dashboard-card--table">
            <div className="quotation-section-heading"><div><h2>Pending invoices</h2><p>Invoices needing collection attention.</p></div><CrmSelect label="" value={pendingInvoiceFilter} onChange={(event) => setPendingInvoiceFilter(event.target.value)}><option value="all">All pending</option><option value="issued">Issued</option><option value="overdue">Overdue</option></CrmSelect></div>
            <div className="crm-dashboard-table-wrap"><table className="crm-data-table"><thead><tr><th>Invoice</th><th>Date</th><th>Total</th><th>Status</th></tr></thead><tbody>{paginatedPendingInvoices.map((inv) => <tr key={inv.id} className="is-clickable" onClick={() => navigate(`/invoices/${inv.id}`)}><td><span className="crm-table-link">{inv.invoice_number}</span></td><td>{formatDate(inv.issue_date)}</td><td>{formatINR(inv.grand_total)}</td><td><span className={`crm-dashboard-status ${isOverdueInvoice(inv) ? 'is-overdue' : ''}`}>{isOverdueInvoice(inv) ? 'Overdue' : inv.status}</span></td></tr>)}{!filteredPendingInvoices.length && <tr><td colSpan="4" className="crm-dashboard__empty">No pending invoices.</td></tr>}</tbody></table></div>
            {!!filteredPendingInvoices.length && <div className="crm-dashboard-pagination"><span>{pendingInvoicesPage * pendingInvoicesRowsPerPage + 1}–{Math.min((pendingInvoicesPage + 1) * pendingInvoicesRowsPerPage, filteredPendingInvoices.length)} of {filteredPendingInvoices.length}</span><div><CrmSelect label="" value={String(pendingInvoicesRowsPerPage)} onChange={handlePendingInvoicesRowsPerPageChange}><option value="5">5 rows</option><option value="10">10 rows</option><option value="15">15 rows</option></CrmSelect><CrmButton size="small" variant="secondary" disabled={pendingInvoicesPage === 0} onClick={() => handlePendingInvoicesPageChange(null, pendingInvoicesPage - 1)}>Previous</CrmButton><CrmButton size="small" variant="secondary" disabled={(pendingInvoicesPage + 1) * pendingInvoicesRowsPerPage >= filteredPendingInvoices.length} onClick={() => handlePendingInvoicesPageChange(null, pendingInvoicesPage + 1)}>Next</CrmButton></div></div>}
          </article>

          <article className="crm-dashboard-card">
            <div className="quotation-section-heading"><div><h2>Contact pipeline</h2><p>Current CRM progression.</p></div><CrmButton size="small" variant="secondary" onClick={() => navigate('/leads')}>Open contacts</CrmButton></div>
            {[{ label:'All contacts', value:leads.length, percent:100 },{ label:'Qualified', value:leadQualifiedCount, percent:leads.length ? Math.round((leadQualifiedCount/leads.length)*100) : 0 },{ label:'Converted', value:leadConvertedCount, percent:leads.length ? Math.round((leadConvertedCount/leads.length)*100) : 0 }].map((item) => <div className="crm-dashboard-pipeline" key={item.label}><div><span>{item.label}</span><strong>{item.value} <small>{item.percent}%</small></strong></div><div className="crm-dashboard-pipeline__bar"><span style={{ width:`${item.percent}%` }} /></div></div>)}
            <div className="crm-dashboard-mini-metrics"><div><span>Avg. invoice</span><strong>{formatINR(averageInvoiceValue)}</strong></div><div><span>Invoice velocity</span><strong>{invoiceVelocityPercent >= 0 ? '+' : ''}{invoiceVelocityPercent}%</strong></div><div><span>Active orders</span><strong>{activeOrdersCount}</strong></div>{isCateringBusiness ? <div><span>Pending KOTs</span><strong>{pendingKotsCount}</strong></div> : null}</div>
          </article>
        </section>

        {!!topPendingInvoicesByAmount.length && <section className="crm-dashboard-card crm-dashboard__attention"><div className="quotation-section-heading"><div><h2>Needs attention</h2><p>Largest currently pending invoice balances.</p></div></div><div className="crm-dashboard-attention-list">{topPendingInvoicesByAmount.map((inv) => <button type="button" key={inv.id} onClick={() => navigate(`/invoices/${inv.id}`)}><span><strong>{inv.invoice_number}</strong><small>{formatDate(inv.issue_date)} · {isOverdueInvoice(inv) ? 'Overdue' : formatStatusLabel(inv.status)}</small></span><b>{formatINR(inv.grand_total)}</b></button>)}</div></section>}
      </>}
    </main>
  )
}

export default Dashboard
